import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { focusRing } from "../../design-system/focusRing";
import { mobile } from "../../design-system/tokens/breakpoints";

// The prototype's only tab metaphor: a grey track holding a white active chip.
// Reactstrap Nav-based tab strips pick this up from _tabs.scss instead; use this
// component where the segments are not a Nav — inside a card header, for instance.
const Track = styled.div`
    display: inline-flex;
    gap: 4px;
    flex: none;
    padding: 3px;
    border-radius: var(--pw-radius-track);
    background: var(--pw-neutral-100);

    ${mobile} {
        display: flex;
        overflow-x: auto;
        -webkit-overflow-scrolling: touch;
    }
`;

const Segment = styled.button`
    ${focusRing}
    padding: 5px 14px;
    border: none;
    border-radius: var(--pw-radius-inner);
    background: ${(props) => (props.$active ? "var(--surface-card)" : "transparent")};
    box-shadow: ${(props) => (props.$active ? "var(--pw-shadow-xs)" : "none")};
    color: ${(props) => (props.$active ? "var(--text-heading)" : "var(--text-muted)")};
    font: inherit;
    font-size: var(--pw-text-hint);
    font-weight: ${(props) => (props.$active ? "var(--pw-weight-semibold)" : "var(--pw-weight-regular)")};
    white-space: nowrap;
    cursor: pointer;
    transition: background var(--pw-transition-fast) var(--pw-ease),
        color var(--pw-transition-fast) var(--pw-ease);

    &:hover:not(:disabled) {
        color: var(--text-heading);
    }
`;

const PathwaySegmentedControl = ({ segments, value, onChange, ariaLabel, dataCy }) => (
    <Track role="tablist" aria-label={ariaLabel} data-cy={dataCy}>
        {segments.map((segment) => (
            <Segment
                key={segment.value}
                type="button"
                role="tab"
                aria-selected={segment.value === value}
                $active={segment.value === value}
                onClick={() => onChange(segment.value)}
                data-cy={dataCy ? `${dataCy}-${segment.value}` : undefined}
            >
                {segment.label}
            </Segment>
        ))}
    </Track>
);

PathwaySegmentedControl.propTypes = {
    segments: PropTypes.arrayOf(
        PropTypes.shape({ value: PropTypes.string.isRequired, label: PropTypes.node.isRequired })
    ).isRequired,
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    ariaLabel: PropTypes.string,
    dataCy: PropTypes.string,
};

export default PathwaySegmentedControl;
